import React, { useMemo } from 'react';
import { MemberAvatarList } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import type { ProjectGanttResponse } from '../types';
import { NOTIFY_EVENT_ITEMS } from './form/shared';
import { fmtDate, isDueToday, isTaskOverdue } from './workspaceHelpers';

interface UserLite {
  id: string;
  name: string;
  avatar?: string;
}

export interface WorkspaceSidePanelProps {
  gantt: ProjectGanttResponse;
  usersById: Map<string, UserLite>;
  /** Navega para a aba de configurações do workspace. */
  onGoToConfig: () => void;
}

/** Anel de progresso em SVG (donut). */
const ProgressRing: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const size = 72;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const dash = (clamped / 100) * circ;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--theme-border)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={clamped === 100 ? '#22c55e' : color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ - dash}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        style={{ fontSize: 15, fontWeight: 700, fill: 'var(--theme-card-text)' }}
      >
        {clamped}%
      </text>
    </svg>
  );
};

const SidePanelCard: React.FC<{ title: string; icon: 'target' | 'users' | 'user'; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div
    className="rounded-lg border p-4"
    style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
      <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const StatChip: React.FC<{ label: string; value: string; tone: 'danger' | 'warn' | 'neutral' }> = ({
  label,
  value,
  tone,
}) => {
  const tones = {
    danger: { bg: 'rgba(239, 68, 68, 0.12)', fg: '#b91c1c' },
    warn: { bg: 'rgba(245, 158, 11, 0.14)', fg: '#b45309' },
    neutral: { bg: 'rgba(148, 163, 184, 0.14)', fg: 'var(--theme-muted-text)' },
  }[tone];
  return (
    <div className="flex items-center justify-between rounded-md px-2.5 py-1.5" style={{ backgroundColor: tones.bg }}>
      <span style={{ fontSize: 12, color: tones.fg }}>{label}</span>
      <span className="font-semibold" style={{ fontSize: 12, color: tones.fg }}>
        {value}
      </span>
    </div>
  );
};

/**
 * Painel lateral do Gantt (protótipo G4): Resumo (anel + chips), Equipe e
 * Contato vinculado. Os botões levam à aba de configurações.
 */
export const WorkspaceSidePanel: React.FC<WorkspaceSidePanelProps> = ({ gantt, usersById, onGoToConfig }) => {
  const { project, progress, overdueCount, tasks } = gantt;
  const { data: contacts } = useContactsDropdown();

  const dueTodayCount = useMemo(() => tasks.filter(isDueToday).length, [tasks]);
  const overdue = overdueCount ?? tasks.filter(isTaskOverdue).length;

  // Próximo marco: primeiro marco não concluído com data futura/hoje; senão o
  // primeiro marco pendente por data.
  const nextMilestone = useMemo(() => {
    const todayMid = new Date();
    const todayMs = Date.UTC(todayMid.getFullYear(), todayMid.getMonth(), todayMid.getDate());
    const pending = tasks
      .filter((t) => t.isMilestone && t.ticket?.statusCategory !== 'resolved' && t.ticket?.statusCategory !== 'closed')
      .map((t) => ({ t, ms: new Date(t.endDate).getTime() }))
      .filter((x) => !Number.isNaN(x.ms))
      .sort((a, b) => a.ms - b.ms);
    const upcoming = pending.find((x) => x.ms >= todayMs) ?? pending[0];
    return upcoming?.t ?? null;
  }, [tasks]);

  const team = useMemo(() => {
    const ids = [project.leaderId, ...(project.memberIds ?? [])].filter(
      (id, i, arr) => !!id && arr.indexOf(id) === i,
    );
    return ids.map((id) => usersById.get(id)).filter((u): u is UserLite => !!u);
  }, [project.leaderId, project.memberIds, usersById]);

  const leader = project.leaderId ? usersById.get(project.leaderId) : undefined;

  const contactNames = useMemo(() => {
    const byId = new Map((contacts ?? []).map((c) => [c.id, c.name] as const));
    return (project.contactIds ?? []).map((id) => byId.get(id)).filter((n): n is string => !!n);
  }, [contacts, project.contactIds]);

  const enabledEvents = NOTIFY_EVENT_ITEMS.filter((e) => project.notifyConfig?.events?.[e.key]);

  return (
    <div className="space-y-4" data-testid="project-ws-side-panel">
      {/* Resumo */}
      <SidePanelCard title="Resumo" icon="target">
        <div className="flex items-center gap-4 mb-3">
          <ProgressRing pct={progress?.pct ?? 0} color={project.color || '#8b5cf6'} />
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
              {progress?.done ?? 0}/{progress?.total ?? 0} tarefas
            </div>
            <div style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>concluídas</div>
          </div>
        </div>
        <div className="space-y-1.5">
          <StatChip label="Vencem hoje" value={String(dueTodayCount)} tone={dueTodayCount > 0 ? 'warn' : 'neutral'} />
          <StatChip label="Atrasadas" value={String(overdue)} tone={overdue > 0 ? 'danger' : 'neutral'} />
          <div
            className="flex items-center justify-between rounded-md px-2.5 py-1.5"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.10)' }}
          >
            <span style={{ fontSize: 12, color: 'var(--theme-primary)' }}>Próximo marco</span>
            <span className="font-semibold text-right truncate ml-2" style={{ fontSize: 12, color: 'var(--theme-primary)' }}>
              {nextMilestone ? `${nextMilestone.ticket?.title ?? 'Marco'} · ${fmtDate(nextMilestone.endDate)}` : '—'}
            </span>
          </div>
        </div>
      </SidePanelCard>

      {/* Equipe */}
      <SidePanelCard title="Equipe" icon="users">
        {team.length > 0 ? (
          <div className="space-y-3">
            <MemberAvatarList members={team} canRemoveMembers={false} size="sm" limit={6} />
            {leader && (
              <div className="flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
                <Icon name="user-check" className="w-3.5 h-3.5" />
                Líder: <span style={{ color: 'var(--theme-card-text)' }}>{leader.name}</span>
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>Sem membros definidos.</p>
        )}
        <button
          type="button"
          onClick={onGoToConfig}
          className="mt-3 flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
          style={{ color: 'var(--theme-primary)' }}
          data-testid="project-ws-side-team-config"
        >
          Gerenciar equipe
          <Icon name="chevron-right" className="w-3.5 h-3.5" />
        </button>
      </SidePanelCard>

      {/* Contato vinculado */}
      <SidePanelCard title="Contato vinculado" icon="user">
        {contactNames.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {contactNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center px-2 py-0.5 rounded-full"
                style={{ fontSize: 11, backgroundColor: 'rgba(148, 163, 184, 0.16)', color: 'var(--theme-card-text)' }}
              >
                {name}
              </span>
            ))}
          </div>
        ) : (
          <p className="mb-3" style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
            Nenhum contato vinculado.
          </p>
        )}
        <div style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
          {enabledEvents.length > 0 ? (
            <span>
              Avisa em:{' '}
              <span style={{ color: 'var(--theme-card-text)' }}>
                {enabledEvents.map((e) => e.label.toLowerCase()).join(', ')}
              </span>
            </span>
          ) : (
            <span>Mensagens automáticas desativadas.</span>
          )}
        </div>
        <button
          type="button"
          onClick={onGoToConfig}
          className="mt-3 flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
          style={{ color: 'var(--theme-primary)' }}
          data-testid="project-ws-side-contact-config"
        >
          Configurar mensagens
          <Icon name="chevron-right" className="w-3.5 h-3.5" />
        </button>
      </SidePanelCard>
    </div>
  );
};

WorkspaceSidePanel.displayName = 'WorkspaceSidePanel';
